import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Mail, Phone, Search, ChevronDown, Download } from "lucide-react";
import { api, errorText, fmtDate } from "@/admin/api";
import { PageHeader, Badge, Empty, Spinner, Button, inputCls } from "@/admin/ui";

const STATUS_TONE = { new: "amber", contacted: "blue", closed: "green" };
const STATUSES = ["new", "contacted", "closed"];

export const toCsv = (rows, cols) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

export const download = (name, text) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  a.download = name;
  a.click();
};

const Row = ({ e, onChange, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(e.note || "");
  const [saving, setSaving] = useState(false);

  const saveNote = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/enquiries/${e.id}`, { note });
      onChange(data);
      toast.success("Note saved");
    } catch (err) { toast.error(errorText(err)); } finally { setSaving(false); }
  };

  return (
    <li className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid={`enquiry-row-${e.id}`}>
      <div className="flex flex-wrap items-center gap-3 p-4 sm:p-5">
        <button onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left" data-testid={`enquiry-toggle-${e.id}`}>
          <p className="text-sm font-semibold text-[#0B1528] truncate">{e.name}</p>
          <p className="text-xs text-slate-500 truncate">{e.package} · {fmtDate(e.created_at)}</p>
        </button>
        <select
          value={e.status || "new"}
          onChange={async (ev) => {
            try {
              const { data } = await api.patch(`/enquiries/${e.id}`, { status: ev.target.value });
              onChange(data);
            } catch (err) { toast.error(errorText(err)); }
          }}
          data-testid={`enquiry-status-${e.id}`}
          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 focus:outline-none focus:border-amber-500"
        >
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <Badge tone={STATUS_TONE[e.status || "new"]}>{e.status || "new"}</Badge>
        <button onClick={() => setOpen(!open)} className="p-1.5 text-slate-400 hover:text-[#0B1528]" aria-label="Expand"><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      </div>
      {open && (
        <div className="border-t border-slate-100 p-4 sm:p-5 grid md:grid-cols-[1fr_280px] gap-5" data-testid={`enquiry-detail-${e.id}`}>
          <div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{e.message}</p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-amber-400 hover:text-amber-600"><Mail className="h-3.5 w-3.5" />{e.email}</a>
              {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-slate-600 hover:border-amber-400 hover:text-amber-600"><Phone className="h-3.5 w-3.5" />{e.phone}</a>}
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1.5 text-slate-400">owner email {e.emailed ? "sent" : "not sent"} · welcome {e.welcomed ? "sent" : "not sent"}</span>
            </div>
          </div>
          <div>
            <textarea value={note} onChange={(ev) => setNote(ev.target.value)} rows={4} placeholder="Private note (only you can see this)" className={`${inputCls} resize-none text-xs`} data-testid={`enquiry-note-${e.id}`} />
            <div className="mt-2 flex gap-2">
              <Button variant="ghost" loading={saving} onClick={saveNote} className="text-xs px-4 py-2" data-testid={`enquiry-save-note-${e.id}`}>Save note</Button>
              <Button variant="danger" onClick={() => onDelete(e)} className="text-xs px-4 py-2" data-testid={`enquiry-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

export default function Enquiries() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => { api.get("/enquiries").then((r) => setItems(r.data.items)); }, []);

  const onChange = (u) => setItems((l) => l.map((x) => (x.id === u.id ? u : x)));
  const onDelete = async (e) => {
    if (!window.confirm(`Delete the enquiry from ${e.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/enquiries/${e.id}`);
      setItems((l) => l.filter((x) => x.id !== e.id));
      toast.success("Enquiry deleted");
    } catch (err) { toast.error(errorText(err)); }
  };

  if (!items) return <Spinner />;
  const shown = items.filter((e) => (filter === "all" || (e.status || "new") === filter) && `${e.name} ${e.email} ${e.package} ${e.message}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-enquiries">
      <PageHeader
        eyebrow="Inbox"
        title="Enquiries"
        sub={`${items.length} total · ${items.filter((e) => (e.status || "new") === "new").length} waiting for a reply`}
        actions={<Button variant="ghost" onClick={() => download("desantrix-enquiries.csv", toCsv(items, ["created_at", "name", "email", "phone", "package", "status", "message", "note"]))} data-testid="enquiries-export"><Download className="h-4 w-4" /> Export CSV</Button>}
      />
      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, message…" className={`${inputCls} pl-10`} data-testid="enquiries-search" />
        </div>
        <div className="flex gap-1.5" data-testid="enquiries-filter">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3.5 py-2 text-xs font-semibold border transition-colors ${filter === s ? "bg-[#0B1528] text-white border-[#0B1528]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>{s}</button>
          ))}
        </div>
      </div>
      {shown.length === 0 ? <Empty>No enquiries match.</Empty> : (
        <ul className="space-y-3" data-testid="enquiries-list">
          {shown.map((e) => <Row key={e.id} e={e} onChange={onChange} onDelete={onDelete} />)}
        </ul>
      )}
    </div>
  );
}
